import { useState } from "react";
import {
  Handshake, Phone, WhatsappLogo, CurrencyInr, ChartBar, UsersThree, Megaphone,
  Buildings, Student, HeadCircuit, Link as LinkIcon, Trophy,
} from "@phosphor-icons/react";
import { GoogleReviews } from "../components/GoogleReviews";
import { LeadForm } from "../components/LeadForm";
import {
  useSeo, Container, PlanHero, SectionTitle, CheckItem, StepCard, FaqList, FeatureCard,
} from "../components/plans/PlanShared";

const ACCENT = "#EC4899";
const WA = "https://wa.me/919986143554?text=" + encodeURIComponent("Hi Unicators, I'd like to join the B2B Affiliate Program. Please share the partnership details.");

const WHO = [
  { icon: HeadCircuit, title: "Coaching Institutes", body: "Offer your NEET students trusted counselling tools & expert support — and earn on every referral." },
  { icon: Student, title: "Independent Counsellors", body: "Extend your services with our data platform and premium counselling, without building it yourself." },
  { icon: Buildings, title: "Schools & Consultants", body: "Add a credible medical-admissions offering for your students and families under your brand." },
];

const BENEFITS = [
  { icon: CurrencyInr, title: "Attractive Commissions", body: "Earn a healthy share on every self-serve subscription and premium counselling admission you refer." },
  { icon: ChartBar, title: "Partner Dashboard", body: "Track referrals, conversions and payouts transparently — full visibility, no black boxes." },
  { icon: Megaphone, title: "Co-Branded Material", body: "Get ready-to-use creatives, links and collateral to promote to your student community." },
  { icon: UsersThree, title: "Dedicated Support", body: "A partner manager helps you onboard, train and maximise results throughout the season." },
  { icon: LinkIcon, title: "Unique Referral Links", body: "Personalised tracking links & codes so every lead and admission is correctly attributed to you." },
  { icon: Trophy, title: "Performance Rewards", body: "Tiered incentives and bonuses for top-performing partners as your referrals grow." },
];

const STEPS = [
  { n: 1, title: "Apply to Join", body: "Share your details and tell us about your student base — we review and approve genuine partners." },
  { n: 2, title: "Onboard & Get Links", body: "Receive your partner dashboard, unique referral links, codes and co-branded material." },
  { n: 3, title: "Refer Students", body: "Recommend Unicators or get a co-branded page to promote self-serve plans and premium counselling to your students and families." },
  { n: 4, title: "Earn & Track Payouts", body: "Get commissions on every conversion, tracked transparently and paid out on schedule." },
];

const FAQ = [
  { q: "Who can join the affiliate program?", a: "Coaching institutes, independent counsellors, schools, education consultants and community leaders with genuine access to NEET aspirants and their families." },
  { q: "How much can I earn?", a: "You earn a commission on both self-serve subscriptions and premium counselling admissions you refer, with tiered bonuses for higher volumes. Exact slabs are shared during onboarding." },
  { q: "Is there any joining fee?", a: "Yes — a one-time joining fee of ₹25,000 gets you onboarded as an official affiliate partner with your dashboard, tracking links and co-branded material. You then earn commissions on every referral." },
  { q: "How are referrals tracked and paid?", a: "Every partner gets unique links and codes, plus a dashboard showing referrals, conversions and payouts. Commissions are settled on a defined schedule." },
];

export default function PlansAffiliate() {
  useSeo(
    "B2B Affiliate Program — Partner with Unicators NEET Counselling",
    "Join the Unicators B2B affiliate program. Coaching institutes, counsellors and consultants earn attractive commissions on every referred NEET counselling subscription and admission.",
  );
  const [leadOpen, setLeadOpen] = useState(false);

  return (
    <div className="bg-white" data-testid="plans-affiliate-page">
      <PlanHero
        accent={ACCENT} bgFrom="#FDF2F8" bgTo="#FCE7F3"
        eyebrow="B2B · Affiliate Program"
        title="Partner with us."
        highlight="Grow together."
        subtitle="Bring trusted NEET counselling tools and expert support to your students — and earn on every referral. A transparent, rewarding partnership built for coaching institutes, counsellors and consultants."
        stats={[
          { value: "₹25,000", label: "Joining fee" },
          { value: "Live", label: "Partner dashboard" },
          { value: "Dedicated", label: "Partner manager" },
        ]}
      >
        <button onClick={() => setLeadOpen(true)} data-testid="hero-join-affiliate"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-bold text-sm uppercase tracking-wide shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: ACCENT }}>
          <Handshake size={16} weight="fill" /> Join as Affiliate
        </button>
        <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#20b957] transition-colors">
          <WhatsappLogo size={18} weight="fill" /> WhatsApp us
        </a>
      </PlanHero>

      {/* Who it's for */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="Who it's for"
            title="Built for education partners"
            sub="If you work with NEET aspirants, you can add real value for them — and a new revenue stream for yourself." />
          <div className="grid sm:grid-cols-3 gap-5">
            {WHO.map((w) => (<FeatureCard key={w.title} {...w} accent={ACCENT} />))}
          </div>
        </Container>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-[#FCE7F3] to-white">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="Why partner with us"
            title="Everything you need to succeed"
            sub="A partnership designed to be transparent, supported and genuinely rewarding." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b) => (<FeatureCard key={b.title} {...b} accent={ACCENT} />))}
          </div>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="How it works"
            title="Start earning in four steps"
            sub="Simple onboarding, transparent tracking, timely payouts." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s) => (<StepCard key={s.n} {...s} accent={ACCENT} />))}
          </div>
        </Container>
      </section>

      {/* CTA band */}
      <section className="py-14">
        <Container>
          <div className="rounded-3xl p-8 sm:p-10 text-center" style={{ background: "linear-gradient(135deg,#FDF2F8,#FCE7F3)" }}>
            <h3 className="text-2xl sm:text-3xl font-black text-[#09090B] font-heading">Ready to become a partner?</h3>
            <p className="mt-2 text-[#52525B] max-w-xl mx-auto">Join a growing network of education partners helping students win their medical seats.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button onClick={() => setLeadOpen(true)} data-testid="affiliate-join-cta"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-bold text-sm uppercase tracking-wide shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: ACCENT }}>
                <Handshake size={16} weight="fill" /> Join as Affiliate
              </button>
              <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="affiliate-whatsapp"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] text-white font-bold text-sm uppercase tracking-wide hover:bg-[#20b957] transition-colors">
                <WhatsappLogo size={18} weight="fill" /> WhatsApp us
              </a>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#FDF2F8]">
        <Container>
          <SectionTitle accent={ACCENT} eyebrow="Good to know" title="Frequently asked questions" />
          <FaqList items={FAQ} accent={ACCENT} />
        </Container>
      </section>

      <GoogleReviews />
      <LeadForm open={leadOpen} onOpenChange={setLeadOpen} plan="B2B Affiliate Program" callbackType="Affiliate" defaultMessage="I am interested to become a associate education counsellor" />
    </div>
  );
}
